import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  CornerDownLeft,
  FileText,
  Film,
  FolderOpen,
  Image as ImageIcon,
  Loader2,
  Mic2,
  UserRound,
  X,
} from 'lucide-react';
import {
  getCharacters,
  getSpace,
  getLibraryCharacters,
  getMyVoices,
  getOrgAssets,
  listSpaces,
  getSpaceNodes,
  searchSpaceNodes,
  listVideoLibrary,
} from '../lib/api';
import './ComposerReferencePicker.css';

export const COMPOSER_REFERENCE_KINDS = [
  { kind: 'character', label: 'Characters', description: 'Reusable people and personas', Icon: UserRound },
  { kind: 'voice', label: 'Voices', description: 'Saved voice identities', Icon: Mic2 },
  { kind: 'image', label: 'Images', description: 'Reusable assets from your library', Icon: ImageIcon },
  { kind: 'video', label: 'Videos', description: 'Completed videos from your library', Icon: Film },
  { kind: 'space', label: 'Spaces', description: 'Themed collections of reusable elements', Icon: FolderOpen },
];

const mentionBoundary = /[\s([{"']/;
export const COMPOSER_REFERENCE_PAGE_SIZE = 20;
const COMPOSER_REFERENCE_SEARCH_DELAY_MS = 220;

function findMentionStart(text) {
  let index = text.lastIndexOf('@');
  while (index >= 0) {
    if (index === 0 || mentionBoundary.test(text[index - 1])) return index;
    index = text.lastIndexOf('@', index - 1);
  }
  return -1;
}

function characterItems(response, source) {
  return (response?.characters || []).map((character) => ({
    kind: 'character',
    id: character.id,
    name: character.name || 'Character',
    previewUrl: character.assetImageUrl || character.imageUrl || null,
    description: [character.personality, character.physicalAppearance].filter(Boolean).join(' · '),
    metadata: source,
    personality: character.personality || '',
    physicalAppearance: character.physicalAppearance || '',
    voiceId: character.voiceId || null,
    voiceName: character.voiceName || '',
    voicePreviewUrl: character.voicePreviewUrl || null,
  }));
}

export async function loadComposerReferenceItems(
  kind,
  query = '',
  limit = COMPOSER_REFERENCE_PAGE_SIZE,
) {
  const search = query.trim() || undefined;

  if (kind === 'space') {
    const response = await listSpaces({ search: search || null });
    return (response?.spaces || [])
      .slice(0, limit)
      .map((space) => {
        const previewItem = space.previewItems?.find((item) => item.thumbnailUrl || item.mediaUrl);
        return {
          kind: 'space',
          id: space.id,
          name: space.name || 'Space',
          previewUrl: previewItem?.thumbnailUrl || previewItem?.mediaUrl || null,
          description: space.description || '',
          metadata: `${space.itemCount || 0} ${space.itemCount === 1 ? 'element' : 'elements'}`,
          itemCount: space.itemCount || 0,
          canOpen: true,
          spaceId: space.id,
          ancestorIds: [],
        };
      });
  }

  if (kind === 'space_item') {
    const response = await listSpaces({ search: null });
    const spaces = (response?.spaces || []).slice(0, 20);
    const details = await Promise.all(spaces.map(async (space) => {
      try { return (await getSpace(space.id))?.space || { ...space, items: [] }; }
      catch { return { ...space, items: [] }; }
    }));
    const normalized = query.trim().toLowerCase();
    const results = [];
    details.forEach((space) => {
      const byId = new Map((space.items || []).map((item) => [item.id, item]));
      const pathFor = (item) => {
        const names = [item.name];
        let parent = item.parentId ? byId.get(item.parentId) : null;
        while (parent) { names.unshift(parent.name); parent = parent.parentId ? byId.get(parent.parentId) : null; }
        return names.join(' / ');
      };
      (space.items || []).forEach((item) => {
        const path = pathFor(item);
        const haystack = `${space.name} ${path} ${item.description || ''}`.toLowerCase();
        if (normalized && !haystack.includes(normalized)) return;
        results.push({
          kind: 'space_item',
          id: item.id,
          name: item.name,
          previewUrl: item.thumbnailUrl || item.mediaUrl || null,
          description: item.description || '',
          metadata: `${space.name} / ${path}`,
          itemType: item.kind || item.mediaType,
        });
      });
    });
    return results.slice(0, limit);
  }

  if (kind === 'character') {
    const sourceLimit = Math.max(1, Math.ceil(limit / 2));
    const request = { search, limit: sourceLimit, offset: 0 };
    const [personal, platform] = await Promise.all([
      getCharacters(request),
      getLibraryCharacters(request),
    ]);
    const byId = new Map();
    [...characterItems(personal, 'My character'), ...characterItems(platform, 'Character library')]
      .forEach((item) => {
        if (item.id && !byId.has(item.id)) byId.set(item.id, item);
      });
    return Array.from(byId.values()).slice(0, limit);
  }

  if (kind === 'voice') {
    const response = await getMyVoices({
      search,
      limit,
      offset: 0,
      attachableOnly: true,
    });
    return (response?.voices || [])
      .filter((voice) => Boolean(voice.previewUrl || voice.preview_url))
      .map((voice) => ({
        kind: 'voice',
        id: voice.id,
        name: voice.name || 'Voice',
        previewUrl: voice.previewUrl || voice.preview_url,
        description: voice.description || '',
        metadata: voice.character?.name ? `Voice for ${voice.character.name}` : 'Saved voice',
      }));
  }

  if (kind === 'image') {
    const response = await getOrgAssets({
      search,
      limit,
      offset: 0,
      mediaType: 'image',
    });
    return (response?.assets || [])
      .filter((asset) => (
        !asset.parentAssetId
        && !asset.parent_asset_id
        && (asset.mediaType || asset.media_type || 'image') === 'image'
        && asset.imageUrl
      ))
      .map((asset) => ({
        kind: 'image',
        id: asset.id,
        name: asset.name || 'Image',
        previewUrl: asset.imageUrl,
        description: asset.prompt || '',
        metadata: 'Reusable asset',
      }));
  }

  if (kind === 'video') {
    const response = await listVideoLibrary({
      page: 1,
      limit,
      search: search || null,
      completedOnly: true,
    });
    return (response?.videos || [])
      .filter((video) => video.status === 'completed' && Boolean(video.videoUrl))
      .map((video) => ({
        kind: 'video',
        id: video.id,
        name: video.name || video.prompt || video.projectName || 'Video',
        previewUrl: video.videoUrl,
        description: video.prompt || '',
        metadata: video.projectName || 'Video library',
        projectName: video.projectName || '',
        prompt: video.prompt || '',
      }));
  }

  return [];
}

async function loadSpaceBrowserItems(stack, query = '', { cursor = null, offset = 0 } = {}) {
  const space = stack[0];
  if (!space) return { items: await loadComposerReferenceItems('space', query), nextCursor: null, total: null };
  const currentFolder = stack[stack.length - 1]?.kind === 'space_node' ? stack[stack.length - 1] : null;
  const response = query.trim()
    ? await searchSpaceNodes(space.id, { query: query.trim(), withinNodeId: currentFolder?.id || null, scope: 'folder', offset, limit: COMPOSER_REFERENCE_PAGE_SIZE })
    : await getSpaceNodes(space.id, { parentId: currentFolder?.id || null, cursor, limit: COMPOSER_REFERENCE_PAGE_SIZE });
  const ancestorIds = stack.filter((entry) => entry.kind === 'space_node').map((entry) => entry.id);
  return { items: (response.items || []).map((item) => ({
    kind: 'space_node',
    id: item.id,
    name: item.name,
    previewUrl: item.thumbnailUrl || item.mediaUrl || null,
    description: item.description || '',
    metadata: item.path || [...stack.map((entry) => entry.name), item.name].join(' / '),
    itemType: item.kind || item.mediaType,
    canOpen: item.kind === 'folder' || item.mediaType === 'folder',
    spaceId: space.id,
    spaceName: space.name,
    ancestorIds,
    content: item.content || '',
    mediaUrl: item.mediaUrl || item.videoUrl || item.audioUrl || item.thumbnailUrl || null,
  })), nextCursor: response.nextCursor || null, total: response.total ?? null };
}

function referenceKey(reference) {
  return `${reference.kind}:${reference.id}`;
}

export function toAttachmentReferences(references = []) {
  return references
    .filter((reference) => reference?.kind && reference?.id)
    .map(({ kind, id, attachAsText }) => ({ kind, id, ...(attachAsText ? { attachAsText: true } : {}) }));
}

export function useComposerReferencePicker({
  enabled = true,
  value,
  onChange,
  textareaRef,
  selectedReferences = [],
  setSelectedReferences,
}) {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('types');
  const [activeKind, setActiveKind] = useState(null);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [itemsByKind, setItemsByKind] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [retryVersion, setRetryVersion] = useState(0);
  const [spaceStack, setSpaceStack] = useState([]);
  const [spaceContinuation, setSpaceContinuation] = useState(null);
  const triggerStartRef = useRef(-1);
  const cursorRef = useRef(0);
  const menuRef = useRef(null);
  const requestRef = useRef(0);

  const close = useCallback(() => {
    requestRef.current += 1;
    setOpen(false);
    setLoading(false);
    setStage('types');
    setActiveKind(null);
    setQuery('');
    setSelectedIndex(0);
    setError('');
    setSpaceStack([]);
    setSpaceContinuation(null);
    triggerStartRef.current = -1;
  }, []);

  useEffect(() => {
    if (!enabled) close();
  }, [close, enabled]);

  useEffect(() => {
    if (!open) return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || textareaRef.current?.contains(event.target)) return;
      close();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [close, open, textareaRef]);

  useEffect(() => {
    if (!open || stage !== 'items' || !activeKind) return undefined;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    const delay = query.trim() ? COMPOSER_REFERENCE_SEARCH_DELAY_MS : 0;
    const timer = window.setTimeout(async () => {
      try {
        let items;
        if (activeKind === 'space') {
          const page = await loadSpaceBrowserItems(spaceStack, query);
          items = page.items;
          setSpaceContinuation({ nextCursor: page.nextCursor, total: page.total });
        } else {
          items = await loadComposerReferenceItems(activeKind, query);
          setSpaceContinuation(null);
        }
        if (requestRef.current !== requestId) return;
        setItemsByKind((current) => ({ ...current, [activeKind]: items }));
      } catch (err) {
        if (requestRef.current !== requestId) return;
        setError(`Could not load ${activeKind} references.`);
      } finally {
        if (requestRef.current === requestId) setLoading(false);
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [activeKind, open, query, retryVersion, spaceStack, stage]);

  const typeOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return COMPOSER_REFERENCE_KINDS;
    return COMPOSER_REFERENCE_KINDS.filter((option) => (
      `${option.label} ${option.description}`.toLowerCase().includes(normalized)
    ));
  }, [query]);

  const itemOptions = useMemo(() => {
    return itemsByKind[activeKind] || [];
  }, [activeKind, itemsByKind]);

  const options = stage === 'types' ? typeOptions : itemOptions;
  const selectedReferenceKeys = useMemo(
    () => new Set(selectedReferences.map(referenceKey)),
    [selectedReferences],
  );

  useEffect(() => {
    setSelectedIndex((current) => Math.min(current, Math.max(0, options.length - 1)));
  }, [options.length]);

  useEffect(() => {
    if (!open || loading || options.length === 0) return;
    const selectedOption = menuRef.current?.querySelector(
      `[data-composer-reference-index="${selectedIndex}"]`,
    );
    if (typeof selectedOption?.scrollIntoView === 'function') {
      selectedOption.scrollIntoView({ block: 'nearest' });
    }
  }, [loading, open, options.length, selectedIndex, stage]);

  const setCaret = useCallback((position) => {
    window.setTimeout(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.focus();
      textarea.setSelectionRange(position, position);
      cursorRef.current = position;
    }, 0);
  }, [textareaRef]);

  const replaceQueryWithBareTrigger = useCallback(() => {
    const start = triggerStartRef.current;
    const cursor = cursorRef.current;
    if (start < 0) return;
    const nextValue = `${value.slice(0, start)}@${value.slice(cursor)}`;
    onChange(nextValue);
    cursorRef.current = start + 1;
    setCaret(start + 1);
  }, [onChange, setCaret, value]);

  const chooseType = useCallback((option) => {
    if (!option) return;
    replaceQueryWithBareTrigger();
    setStage('items');
    setActiveKind(option.kind);
    setQuery('');
    setSelectedIndex(0);
    setError('');
    if (option.kind === 'space') setSpaceStack([]);
  }, [replaceQueryWithBareTrigger]);

  const chooseItem = useCallback((item) => {
    if (!item || triggerStartRef.current < 0) return;
    const start = triggerStartRef.current;
    const cursor = cursorRef.current;
    const nextValue = `${value.slice(0, start)}${value.slice(cursor)}`;
    onChange(nextValue);
    setSelectedReferences((current) => {
      const existingIndex = current.findIndex((reference) => referenceKey(reference) === referenceKey(item));
      if (existingIndex >= 0) {
        if (Boolean(current[existingIndex].attachAsText) === Boolean(item.attachAsText)) return current;
        return current.map((reference, index) => index === existingIndex ? item : reference);
      }
      if (item.kind === 'space') {
        return [...current.filter((reference) => reference.spaceId !== item.id), item];
      }
      if (item.kind === 'space_node') {
        if (current.some((reference) => reference.kind === 'space' && reference.id === item.spaceId)) return current;
        if (current.some((reference) => reference.kind === 'space_node' && item.ancestorIds?.includes(reference.id))) return current;
        const remaining = item.canOpen
          ? current.filter((reference) => !(reference.kind === 'space_node' && reference.spaceId === item.spaceId && reference.ancestorIds?.includes(item.id)))
          : current;
        return [...remaining, item];
      }
      return [...current, item];
    });
    close();
    setCaret(start);
  }, [close, onChange, setCaret, setSelectedReferences, value]);

  const openContainer = useCallback((item) => {
    if (!item?.canOpen) return;
    setSpaceStack((current) => [...current, item]);
    setQuery('');
    setSelectedIndex(0);
    setError('');
  }, []);

  const handlePromptChange = useCallback((event) => {
    const nextValue = event.target.value;
    const cursor = event.target.selectionStart ?? nextValue.length;
    cursorRef.current = cursor;
    onChange(nextValue);
    if (!enabled) return;

    const beforeCursor = nextValue.slice(0, cursor);
    const mentionStart = findMentionStart(beforeCursor);
    if (mentionStart < 0) {
      close();
      return;
    }

    if (!open || mentionStart !== triggerStartRef.current) {
      triggerStartRef.current = mentionStart;
      setStage('types');
      setActiveKind(null);
    }
    setQuery(beforeCursor.slice(mentionStart + 1));
    setSelectedIndex(0);
    setOpen(true);
  }, [close, enabled, onChange, open]);

  const handleSelection = useCallback((event) => {
    cursorRef.current = event.currentTarget.selectionStart ?? 0;
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!open) return false;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((current) => Math.min(current + 1, Math.max(0, options.length - 1)));
      return true;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((current) => Math.max(0, current - 1));
      return true;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (stage === 'types') chooseType(options[selectedIndex]);
      else chooseItem(options[selectedIndex]);
      return true;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return true;
    }
    if (event.key === 'Backspace' && stage === 'items' && query.length === 0) {
      event.preventDefault();
      setStage('types');
      setActiveKind(null);
      setSelectedIndex(0);
      setError('');
      return true;
    }
    return false;
  }, [chooseItem, chooseType, close, open, options, query.length, selectedIndex, stage]);

  const returnToTypes = useCallback(() => {
    if (activeKind === 'space' && spaceStack.length) {
      setSpaceStack((current) => current.slice(0, -1));
      setQuery('');
      setSelectedIndex(0);
      setError('');
      return;
    }
    setStage('types');
    setActiveKind(null);
    setQuery('');
    setSelectedIndex(0);
    setError('');
    setCaret(triggerStartRef.current + 1);
  }, [activeKind, setCaret, spaceStack.length]);

  const retry = useCallback(() => {
    setRetryVersion((current) => current + 1);
  }, []);

  const loadMore = useCallback(async () => {
    if (loading || activeKind !== 'space' || !spaceStack[0]) return;
    const current = itemsByKind.space || [];
    if (!spaceContinuation?.nextCursor && !(Number.isFinite(spaceContinuation?.total) && current.length < spaceContinuation.total)) return;
    setLoading(true); setError('');
    try {
      const page = await loadSpaceBrowserItems(spaceStack, query, { cursor: spaceContinuation.nextCursor, offset: current.length });
      setItemsByKind((value) => ({ ...value, space: [...(value.space || []), ...page.items] }));
      setSpaceContinuation({ nextCursor: page.nextCursor, total: page.total });
    } catch (err) { setError('Could not load more Space items.'); }
    finally { setLoading(false); }
  }, [activeKind, itemsByKind.space, loading, query, spaceContinuation, spaceStack]);

  return {
    activeKind,
    chooseItem,
    chooseType,
    error,
    handleKeyDown,
    handlePromptChange,
    handleSelection,
    itemOptions,
    loading,
    loadMore,
    menuRef,
    open,
    openContainer,
    options,
    query,
    retry,
    returnToTypes,
    selectedIndex,
    selectedReferenceKeys,
    setSelectedIndex,
    stage,
    spaceStack,
    spaceContinuation,
    typeOptions,
  };
}

function ReferenceIcon({ kind, size = 15 }) {
  if (kind === 'folder') return <FolderOpen size={size} aria-hidden="true" />;
  if (kind === 'text' || kind === 'skill' || kind === 'space_node') return <FileText size={size} aria-hidden="true" />;
  const option = COMPOSER_REFERENCE_KINDS.find((candidate) => candidate.kind === kind);
  const Icon = option?.Icon || ImageIcon;
  return <Icon size={size} aria-hidden="true" />;
}

export function ComposerReferenceMenu({ picker, className = '' }) {
  const [previewItem, setPreviewItem] = useState(null);
  if (!picker.open) return null;
  const activeLabel = COMPOSER_REFERENCE_KINDS.find((option) => option.kind === picker.activeKind)?.label;

  return <>
    <div
      ref={picker.menuRef}
      className={`composer-reference-menu${className ? ` ${className}` : ''}`}
      role="listbox"
      aria-label={picker.stage === 'types' ? 'Reference types' : `${activeLabel || 'Reference'} results`}
    >
      <div className="composer-reference-menu__header">
        {picker.stage === 'items' ? (
          <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={picker.returnToTypes} aria-label="Back to reference types">
            <ArrowLeft size={12} />
            <span>{picker.spaceStack?.length ? picker.spaceStack.map((entry) => entry.name).join(' / ') : activeLabel}</span>
          </button>
        ) : (
          <span>Attach reference</span>
        )}
        <small>{picker.query ? `“${picker.query}”` : picker.stage === 'items' ? 'Type to search' : 'Choose a type'}</small>
      </div>

      <div className="composer-reference-menu__list">
        {picker.loading && picker.stage === 'items' ? (
          <div className="composer-reference-menu__state"><Loader2 className="composer-reference-menu__spinner" size={14} />Loading {activeLabel?.toLowerCase()}…</div>
        ) : picker.error ? (
          <div className="composer-reference-menu__state composer-reference-menu__state--error">
            <span>{picker.error}</span>
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={picker.retry}>Retry</button>
          </div>
        ) : picker.options.length === 0 ? (
          <div className="composer-reference-menu__state">No matching {picker.stage === 'types' ? 'reference types' : activeLabel?.toLowerCase()}.</div>
        ) : picker.stage === 'types' ? picker.typeOptions.map((option, index) => (
          <button
            type="button"
            role="option"
            aria-selected={index === picker.selectedIndex}
            data-composer-reference-index={index}
            className={`composer-reference-menu__row composer-reference-menu__row--type${index === picker.selectedIndex ? ' is-selected' : ''}`}
            key={option.kind}
            onMouseDown={(event) => event.preventDefault()}
            onMouseEnter={() => picker.setSelectedIndex(index)}
            onClick={() => picker.chooseType(option)}
          >
            <span className="composer-reference-menu__kind"><option.Icon size={15} /></span>
            <span className="composer-reference-menu__copy"><strong>{option.label}</strong><small>{option.description}</small></span>
            <CornerDownLeft size={12} className="composer-reference-menu__enter" />
          </button>
        )) : picker.itemOptions.map((item, index) => (
          <div
            role="option"
            aria-selected={index === picker.selectedIndex}
            data-composer-reference-index={index}
            className={`composer-reference-menu__row composer-reference-menu__row--browsable${index === picker.selectedIndex ? ' is-selected' : ''}${picker.selectedReferenceKeys.has(referenceKey(item)) ? ' is-attached' : ''}`}
            key={referenceKey(item)}
            onMouseEnter={() => picker.setSelectedIndex(index)}
            onClick={() => picker.chooseItem(item)}
          >
            <button type="button" className="composer-reference-menu__attach" onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); picker.chooseItem(item); }} aria-label={`Attach ${item.name}`}>
              <span className={`composer-reference-menu__thumb composer-reference-menu__thumb--${item.kind}`}>
                {item.kind === 'video' ? <video src={item.previewUrl} muted playsInline preload="metadata" /> : item.previewUrl && item.kind !== 'voice' ? <img src={item.previewUrl} alt="" loading="lazy" /> : <ReferenceIcon kind={item.itemType || item.kind} />}
              </span>
              <span className="composer-reference-menu__copy"><strong>{item.name}</strong><small>{item.metadata || item.description || item.kind}</small></span>
              {picker.selectedReferenceKeys.has(referenceKey(item)) && <span className="composer-reference-menu__attached"><Check size={10} />Attached</span>}
            </button>
            {item.itemType === 'skill' && <button type="button" className="composer-reference-menu__as-text" onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); picker.chooseItem({ ...item, attachAsText: true }); }}>As text</button>}
            {!item.canOpen && item.kind === 'space_node' && <button type="button" className="composer-reference-menu__open" onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); setPreviewItem(item); }}>Preview</button>}
            {item.canOpen && <button type="button" className="composer-reference-menu__open" onMouseDown={(event) => event.preventDefault()} onClick={(event) => { event.stopPropagation(); picker.openContainer(item); }}>Open <ChevronRight size={12} /></button>}
          </div>
        ))}
        {picker.stage === 'items' && picker.activeKind === 'space' && picker.spaceStack?.length > 0 && (picker.spaceContinuation?.nextCursor || (Number.isFinite(picker.spaceContinuation?.total) && picker.itemOptions.length < picker.spaceContinuation.total)) && <button type="button" className="composer-reference-menu__load-more" disabled={picker.loading} onMouseDown={(event) => event.preventDefault()} onClick={picker.loadMore}>{picker.loading ? 'Loading…' : 'Load more'}</button>}
      </div>

      <div className="composer-reference-menu__footer"><span>↑↓ Navigate</span><span>↵ Select</span><span>Esc Close</span></div>
    </div>
    {previewItem && <div className="composer-reference-preview-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setPreviewItem(null)}><section className="composer-reference-preview" role="dialog" aria-modal="true" aria-label={`Preview ${previewItem.name}`}><header><div><small>{previewItem.itemType} · {previewItem.metadata}</small><h3>{previewItem.name}</h3></div><button type="button" onClick={() => setPreviewItem(null)} aria-label="Close preview"><X size={15} /></button></header><div className="composer-reference-preview__body">{previewItem.itemType === 'text' || previewItem.itemType === 'skill' ? <pre>{previewItem.content || 'This file is empty.'}</pre> : previewItem.itemType === 'video' && previewItem.mediaUrl ? <video src={previewItem.mediaUrl} controls playsInline /> : previewItem.itemType === 'audio' && previewItem.mediaUrl ? <audio src={previewItem.mediaUrl} controls /> : previewItem.previewUrl || previewItem.mediaUrl ? <img src={previewItem.previewUrl || previewItem.mediaUrl} alt={previewItem.name} /> : <p>{previewItem.description || 'No preview is available.'}</p>}</div><footer><button type="button" onClick={() => { picker.chooseItem(previewItem); setPreviewItem(null); }}>Attach</button></footer></section></div>}
  </>;
}

export function ComposerReferenceChips({ references, onRemove, className = '', disabled = false }) {
  if (!references?.length) return null;
  return (
    <div className={`composer-reference-chips${className ? ` ${className}` : ''}`} aria-label="Attached library references">
      {references.map((reference) => (
        <div className="composer-reference-chip" key={referenceKey(reference)}>
          <span className={`composer-reference-chip__preview composer-reference-chip__preview--${reference.kind}`}>
            {reference.kind === 'video' ? <video src={reference.previewUrl} muted playsInline preload="metadata" /> : reference.previewUrl && reference.kind !== 'voice' ? <img src={reference.previewUrl} alt="" /> : <ReferenceIcon kind={reference.kind} size={13} />}
          </span>
          <span className="composer-reference-chip__copy"><small title={reference.metadata || ''}>{reference.itemType || reference.kind}{reference.attachAsText ? ' as text' : ''}{reference.metadata ? ` · ${reference.metadata}` : ''}</small><strong>{reference.name}</strong></span>
          <button type="button" disabled={disabled} onClick={() => onRemove(reference)} aria-label={`Remove ${reference.name}`}><X size={11} /></button>
        </div>
      ))}
    </div>
  );
}
